/**
 * Acesso e carteira — quem enxerga quais leads no funil.
 *
 * ── Extraído da `ConfigCrmPage` em 2026-08-03 (K-3)
 *
 * A `ConfigCrmPage` tinha **1905 linhas** e 10 abas num arquivo só. Cada aba
 * virou um item do menu da Configurações, e o painel da casca renderiza este
 * componente direto — sem passar por ela.
 *
 * A extração é passo PRÓPRIO, antes da migração, de propósito: separar "mover
 * código" de "mudar comportamento" é o que permite ler o diff de cada um.
 */
/**
 * CRM v2 — Configurações > Comercial > CRM (PRD §7).
 *
 * TODO parâmetro do Comercial mora aqui: catálogo de serviços · fases ·
 * status de lead · origens · campos obrigatórios por fase · tipos de
 * atividade · motivos de perda/descarte · documentos do Ganho · tags ·
 * construtor de automações · visibilidade por perfil · carteira (ownership).
 * (Metas têm tela própria em Comercial → Metas.)
 */

import {useState} from 'react';
import { resumoDaCarteira, fraseDoRoteamento } from './resumo-da-carteira';
import { LinhaDeContrato } from './LinhaDeContrato';
import { useUiPreference } from '@/hooks';

import {Card, HelpTip, Input, Select, SelectOption, Switch, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, useToast} from '@/components/ui';
import {cn} from '@/lib/cn';
import {useConfigGeralActions, useCrmVisibilidade, useOwnership, useCarteiraAutomatica, useSalvarCarteiraAutomatica} from '@/lib/crm/crm-store';
// O14: dropdowns de usuário do comercial listam SÓ o departamento comercial.
import {useUsersComercial} from '@/lib/admin-store';
// O7: config completa do Orçamento (markups, régua, faixas, kits, taxas).
// A7: Modelos de orçamento nomeados (aba própria — grid de cards + drawer).
// R3: Reciclagem de leads (aba própria — leads parados voltam pro pool/redistribuição).
// Bloco C: Lembretes de atividade agendada (liga/desliga + antecedência).
import type { IndicadoresNivel } from '@/lib/crm/crm-store';


export function AbaAcesso() {
  const vis = useCrmVisibilidade();
  const geral = useConfigGeralActions();
  const [busca, setBusca] = useState('');
  /**
   * 🔴 U2 — o RECORTE: `''` = todos, `'sem'` = quem não tem dono, id = aquele
   * vendedor. ⚠️ Estado transiente, em React state: preferência de tela vive no
   * `useUiPreference`, e isto não é preferência — é onde a pessoa está agora.
   */
  const [recorte, setRecorte] = useState('');
  /**
   * 🔴 19/10/2026 — a chave "Só com contrato". ✅ É preferência de tela (a pessoa volta e encontra do mesmo jeito), então mora no
   * `useUiPreference` (backend, por usuário — o padrão de `clientes-v2.filtro-etiqueta`). ❌ Zero localStorage.
   */
  const [soComContrato, setSoComContrato] = useUiPreference<boolean>('crm-carteira.so-com-contrato', false);
  const carteira = useOwnership(busca, recorte, soComContrato);
  const users = useUsersComercial();
  const toast = useToast();
  // CL5: carteira automática + completude configurável do Ganho.
  const carteiraAuto = useCarteiraAutomatica();
  const salvarCarteiraAuto = useSalvarCarteiraAutomatica();

  /**

   * 🔴 #458: o resumo da carteira — um motor, dois consumidores (a frase do

   * roteamento e os chips do filtro). ⚠️ `FiltroDaCarteira` calculava `total` e

   * `sem` por conta própria; ele passou a consumir o mesmo motor no MESMO

   * commit, senão os dois números da mesma tela teriam duas fontes (Regra 7).

   */

  const resumo = resumoDaCarteira(carteira.data?.contagens ?? {});


  return (
    <div className="grid md:grid-cols-2 gap-4 items-start">
      <Card variant="flat" className="p-4 min-w-0">
        <h3 className="text-base font-bold text-ink-1 dark:text-ink-dark-1 mb-1 inline-flex items-center gap-1.5">
          Visibilidade por perfil
          <HelpTip texto="Controla o que cada tipo de usuário enxerga nas telas do Comercial: só o que é dele ou o time inteiro. Quem é do departamento Orçamentista vê todos os cards do funil, para orçar qualquer oportunidade." />
        </h3>
        <p className="text-sm text-ink-3 dark:text-ink-dark-3 mb-3">
          Quem enxerga o quê no Comercial. “Só os próprios” = vê os cards/leads/números dele + o ranking da
          equipe. Administradores sempre veem tudo.
        </p>
        <div className="space-y-2">
          {(vis.data?.perfis ?? []).map((p) => (
            <div key={p.slug} className="rounded-lg border border-line-soft dark:border-line-soft-dark px-3 py-2.5 space-y-2">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <span className="flex-1 min-w-0 break-words text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                  {p.nome}
                </span>
                <Select
                  size="sm"
                  value={p.escopo}
                  onChange={(e) =>
                    geral.salvarVisibilidade.mutate(
                      { perfil: p.slug, escopo: e.target.value as 'proprios' | 'todos' },
                      { onError: () => toast.show('Não foi possível salvar a visibilidade.', { variant: 'error' }) },
                    )
                  }
                  wrapperClassName="w-44 shrink-0"
                  aria-label={`Visibilidade do perfil ${p.nome}`}
                >
                  <SelectOption value="proprios">Só os próprios</SelectOption>
                  <SelectOption value="todos">Tudo</SelectOption>
                </Select>
              </div>
              {/* O16: o que este perfil enxerga nos INDICADORES. */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <span className="flex-1 min-w-0 text-sm text-ink-3 dark:text-ink-dark-3 inline-flex items-center gap-1.5">
                  Indicadores
                  <HelpTip texto="Nível de detalhe que este perfil vê na tela de Indicadores — dos próprios números até o consolidado de toda a equipe." />
                </span>
                <Select
                  size="sm"
                  value={p.indicadoresNivel}
                  onChange={(e) =>
                    geral.salvarVisibilidade.mutate(
                      {
                        perfil: p.slug,
                        indicadoresNivel: e.target.value as IndicadoresNivel,
                      },
                      { onError: () => toast.show('Não foi possível salvar a visibilidade.', { variant: 'error' }) },
                    )
                  }
                  wrapperClassName="w-56 shrink-0 max-w-full"
                  aria-label={`Visibilidade dos Indicadores do perfil ${p.nome}`}
                >
                  <SelectOption value="proprios">Só os próprios números</SelectOption>
                  <SelectOption value="ranking_nomeado">+ ranking com nomes</SelectOption>
                  <SelectOption value="ranking_anonimo">+ ranking sem nomes</SelectOption>
                  <SelectOption value="equipe">+ consolidado da equipe</SelectOption>
                  <SelectOption value="tudo">Tudo (igual administrador)</SelectOption>
                </Select>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* 🔴 U2 — a carteira ocupa as DUAS colunas: ela é a tabela larga desta
          tela, e o canônico manda tabela larga usar a largura inteira. */}
      <Card variant="flat" className="p-4 min-w-0 md:col-span-2">
        <h3 className="text-base font-bold text-ink-1 dark:text-ink-dark-1 mb-1 inline-flex items-center gap-1.5">
          Carteira de clientes (vendedor responsável)
          <HelpTip texto="Cada cliente pode ter um vendedor dono. Quando surge oportunidade nova daquele cliente, ela já cai com esse vendedor. A lista traz só quem tem a etiqueta Cliente: fornecedor, transportadora e funcionário moram no mesmo cadastro e não têm vendedor dono. Os botões acima da lista recortam a carteira e dizem quantos clientes cada um tem — comece por Sem vendedor. Os números acompanham a busca: procurando um nome, eles contam só dentro do que a busca achou." />
        </h3>
        {/* 🔴 #458 (01/10/2026): esta frase prometia para 513 e cumpria para 13.
            Medido no smoke do U2 em 29/09 — 500 clientes sem dono. ✅ Agora ela
            diz PARA QUANTOS vale. ⚠️ Sem alarme: os sem dono são o trabalho
            normal, e um aviso vermelho permanente ensina a ignorar avisos. */}
        <p className="text-sm text-ink-3 dark:text-ink-dark-3 mb-1" data-testid="carteira-roteamento">
          {fraseDoRoteamento(resumo, soComContrato)}
        </p>
        {/* ⚠️ A contagem fica ao lado da frase, não num cartão próprio: ela
            QUALIFICA a promessa, e separá-las deixaria a promessa sozinha de
            novo — que é exatamente como o defeito nasceu. */}
        {resumo.total > 0 && (
          <p className="text-xs text-ink-3 dark:text-ink-dark-3 mb-3" data-testid="carteira-com-sem-dono">
            <b className="text-ink-2 dark:text-ink-dark-2">{resumo.comDono}</b> com vendedor dono ·{' '}
            <b className="text-ink-2 dark:text-ink-dark-2">{resumo.semDono}</b> sem dono
            {/* 19/10 (item A): com a chave ligada, a linha diz de qual conjunto fala — texto literal do handoff. */}
            {soComContrato
              ? ', entre os clientes com contrato'
              : resumo.semDono > 0
                ? ' — use o filtro “Sem vendedor” para atribuir.'
                : ''}
          </p>
        )}
        {/* CL5: carteira automática — cliente criado na proposta entra na carteira do criador. */}
        {/* 🔴 19/10/2026 (item B): o texto de apoio das duas chaves era `inline-flex` e corria na MESMA linha do rótulo, que
            também é `inline-flex` ("Só com contratoMostra só quem…"). ✅ Ele vira bloco (`flex`) e desce para a linha de baixo.
            ⚠️ O defeito era desta tela, não do `<Switch>`: o `hint` do próprio componente já sai empilhado; mudar o componente
            mexeria nas outras 45 telas que o usam sem consertar nada nelas. */}
        <div className="mb-3 rounded-lg border border-line-soft dark:border-line-soft-dark px-3 py-2.5">
          <Switch
            label="Cliente novo entra na carteira de quem cadastrou"
            checked={carteiraAuto.data?.ativa ?? true}
            onChange={(e) =>
              salvarCarteiraAuto.mutate(e.target.checked, {
                onError: () => toast.show('Não foi possível salvar.', { variant: 'error' }),
              })
            }
          />
          <p className="mt-1.5 text-xs text-ink-3 dark:text-ink-dark-3 flex flex-wrap items-center gap-1.5" data-testid="carteira-chave-apoio">
            Ao ligar, o cliente cadastrado no cadastro rápido da proposta já nasce na sua carteira.
            <HelpTip texto="Ligada: o cliente novo entra na carteira de quem o cadastrou na proposta. Desligada: nasce sem dono e um administrador atribui depois. Vale só pra novos cadastros." />
          </p>
        </div>
        <Input size="sm" placeholder="Buscar cliente…" value={busca} onChange={(e) => setBusca(e.target.value)} className="mb-3" />
        {/* 🔴 19/10/2026 — "Só com contrato": a mesma linha e o mesmo componente da chave da carteira automática, acima dos
            botões de vendedor (decisão do Matheus: chave liga/desliga, não botão junto dos vendedores). */}
        <div className="mb-3 rounded-lg border border-line-soft dark:border-line-soft-dark px-3 py-2.5">
          <Switch
            label="Só com contrato"
            checked={soComContrato}
            onChange={(e) => setSoComContrato(e.target.checked)}
            data-testid="carteira-so-com-contrato"
          />
          <p className="mt-1.5 text-xs text-ink-3 dark:text-ink-dark-3 flex flex-wrap items-center gap-1.5" data-testid="carteira-chave-apoio">
            Mostra só quem tem contrato ativo, em implantação ou em aviso prévio.
            <HelpTip texto="Conta o contrato da ficha do cliente com situação Ativo, Em implantação ou Aviso prévio. Contrato encerrado não entra. Vale junto com o vendedor escolhido e com a busca." />
          </p>
        </div>
        <FiltroDaCarteira
          contagens={carteira.data?.contagens ?? {}}
          vendedores={users}
          recorte={recorte}
          onRecortar={setRecorte}
        />
        {/* BUGS #92 — a tela mostrava 100 de 761 e CALAVA. Quem procurava
            alguém do 101º em diante concluía que o cliente não estava
            cadastrado, e ia cadastrar de novo. Dizer o número não resolve o
            teto, mas transforma "não existe" em "refine a busca" — que é a
            diferença entre um cadastro duplicado e um segundo de digitação. */}
        {carteira.data && carteira.data.total > carteira.data.items.length && (
          <p className="mb-3 text-xs text-ink-3 dark:text-ink-dark-3" data-testid="carteira-truncada">
            Mostrando {carteira.data.items.length} de {carteira.data.total} clientes{soComContrato ? ' com contrato' : ''} — use a busca
            para encontrar quem não está na lista.
          </p>
        )}
        {/* 19/10: com a chave ligada a frase aparece sempre — ela diz que a lista está recortada. */}
        {carteira.data && soComContrato && carteira.data.total <= carteira.data.items.length && (
          <p className="mb-3 text-xs text-ink-3 dark:text-ink-dark-3" data-testid="carteira-mostrando-com-contrato">
            Mostrando {carteira.data.items.length} de {carteira.data.total} clientes com contrato.
          </p>
        )}
        {/* U1 (28/09/2026) — a lista passou de 774 para 513 porque fornecedor,
            transportadora e funcionário moram no MESMO cadastro e não têm
            vendedor dono. 🔴 A frase existe porque o corte é invisível: sem
            ela, quem procurasse um fornecedor aqui concluiria que o cadastro
            sumiu. É o #92 outra vez — transformar "não existe" em "não é
            desta lista" é a diferença entre um cadastro duplicado e um
            segundo de leitura. */}
        <p className="mb-3 text-xs text-ink-3 dark:text-ink-dark-3" data-testid="carteira-so-clientes">
          Só quem tem a etiqueta <b>Cliente</b> entra aqui. Fornecedor, transportadora e funcionário
          ficam de fora — eles não têm vendedor dono.
        </p>
        {/*
          🔴🔴 **U2 (29/09/2026) — A CARTEIRA VIROU TABELA LARGA.**

          ⚠️ O cartão empilhado cortava o nome com `truncate` **numa coluna de
          metade de tela**, e o que se perdia era justamente o fim da razão
          social — que é onde mora o que distingue *"CONDOMINIO RESIDENCIAL
          TERRAS DO FONTANARIO I"* de *"… II"*. 🔴 **Nome cortado numa tela de
          ATRIBUIR dono é o defeito que produz a atribuição errada.**

          ✅ Agora o nome vem INTEIRO (quebra em duas linhas se precisar) e a
          segunda linha traz **CNPJ e cidade** — a base tem razão social
          repetida, e a cidade é o que desempata olhando.
        */}
        <div className="max-h-[420px] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead>Vendedor responsável</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(carteira.data?.items ?? []).map((c) => (
                <TableRow key={c.clienteId}>
                  <TableCell>
                    {/* A5: razão social é o rótulo principal — na base importada o
                        nome fantasia costuma ser um código curto (ex.: "07 35"). */}
                    <span className="block text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                      {c.razaoSocial || c.nomeFantasia}
                    </span>
                    {/* ⚠️ A linha some inteira quando não há nada para dizer —
                        um " · " órfão afirma que falta um dado que nunca houve. */}
                    {[c.nomeFantasia, c.cnpj, [c.cidade, c.uf].filter(Boolean).join('/')]
                      .filter(Boolean)
                      .join(' · ') && (
                      <span className="block text-xs text-ink-3 dark:text-ink-dark-3">
                        {[c.nomeFantasia, c.cnpj, [c.cidade, c.uf].filter(Boolean).join('/')]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    )}
                    {/* 🔴 19/10/2026: o contrato e o escopo — com a chave ligada ou desligada. */}
                    <LinhaDeContrato contratos={c.contratos ?? []} />
                  </TableCell>
                  <TableCell>
                    <Select
                      size="sm"
                      value={c.vendedorResponsavelId ?? ''}
                      onChange={async (e) => {
                        try {
                          await geral.salvarOwnership.mutateAsync({
                            clienteId: c.clienteId,
                            vendedorResponsavelId: e.target.value || null,
                          });
                        } catch {
                          toast.show('Não foi possível salvar.', { variant: 'error' });
                        }
                      }}
                      wrapperClassName="w-48"
                      aria-label={`Vendedor de ${c.razaoSocial}`}
                    >
                      <SelectOption value="">Sem vendedor</SelectOption>
                      {users.map((u) => (
                        <SelectOption key={u.id} value={u.id}>
                          {u.nome}
                        </SelectOption>
                      ))}
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {(carteira.data?.items ?? []).length === 0 && (
            /* ⚠️ O vazio DIZ por quê, e DE QUAL RECORTE fala. "Nenhum cliente
               encontrado" sozinho era honesto antes do corte do U1, ficou
               ambíguo depois dele, e com o filtro do U2 ficaria enganoso: quem
               recortasse um vendedor sem cliente leria "não há clientes". */
            <p className="py-3 text-sm text-ink-3 dark:text-ink-dark-3">
              {soComContrato
                ? 'Nenhum cliente com contrato ativo, em implantação ou em aviso prévio neste recorte. Desligue "Só com contrato" para ver todos.'
                : busca
                ? 'Nenhum cliente com esse nome na carteira. Fornecedor, transportadora e funcionário não entram nesta lista — procure na tela de Clientes.'
                : recorte === 'sem'
                  ? 'Nenhum cliente sem vendedor — a carteira está toda atribuída.'
                  : recorte
                    ? `${users.find((u) => u.id === recorte)?.nome ?? 'Este vendedor'} ainda não tem cliente na carteira.`
                    : 'Nenhum cliente encontrado.'}
            </p>
          )}
        </div>
      </Card>

      {/* 14/09/2026 (mig 606): a completude configurável do Ganho SAIU — o Ganho
          exige só CNPJ/CPF (decisão do Matheus). A régua de cadastro continua na
          ficha (Configurações › Preenchimento obrigatório). */}
      <Card variant="flat" className="p-4 min-w-0" data-testid="ganho-so-documento">
        <h3 className="text-base font-bold text-ink-1 dark:text-ink-dark-1 mb-1 inline-flex items-center gap-1.5">
          O que o cliente precisa ter pro Ganho
          <HelpTip texto="Desde 14/09/2026 o Ganho exige só o cliente do cadastro com CNPJ ou CPF. O resto da ficha (telefone, endereço, nº de unidades, e-mail para NF-e) continua marcado como obrigatório em Configurações › Preenchimento obrigatório, aparece no chip 'incompleto' da lista de clientes e não pode ser esvaziado — mas não barra mais o Ganho." />
        </h3>
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">
          <b>CNPJ ou CPF</b> — e nada mais. O projeto é planejamento de obra, não documento fiscal; o que a nota
          precisa é cobrado na ficha, não no Ganho.
        </p>
      </Card>
    </div>
  );
}

/**
 * 🔴🔴 U2 — O RECORTE DA CARTEIRA, com o número de cada um.
 *
 * > O pedido: *"filtro 'Sem vendedor' com a contagem por vendedor"*.
 *
 * ⚠️ **O número é o que faz o filtro valer**: *"Sem vendedor"* sozinho obriga a
 * clicar para descobrir se há trabalho ali. Com *"Sem vendedor (12)"*, a
 * resposta está na tela antes do clique.
 *
 * 🔴 **ZERO NÃO É UM LUGAR PARA IR** (régua de 26/09): vendedor sem nenhum
 * cliente **não vira botão** — vira texto. Botão desabilitado ainda afirma
 * *"aqui haveria algo"* e convida a tentar; texto não convida.
 *
 * 🔴 **As contagens vêm do MESMO `WHERE` da lista** (REGRA 7): não há como
 * *"Sem vendedor (12)"* abrir uma lista de 9.
 */
function FiltroDaCarteira({
  contagens,
  vendedores,
  recorte,
  onRecortar,
}: {
  contagens: Record<string, number>;
  vendedores: Array<{ id: string; nome: string }>;
  recorte: string;
  onRecortar: (v: string) => void;
}) {
  // 🔴 #458: os números vêm do MESMO motor da frase do roteamento — duas fontes
  // para a mesma contagem na mesma tela é a Regra 7 quebrada.
  const { total, semDono: sem } = resumoDaCarteira(contagens);

  const chip = (chave: string, rotulo: string, n: number) => {
    const aceso = recorte === chave;
    // 🔴 Zero não é destino: vira texto, sem cursor, sem foco por teclado.
    if (n === 0 && chave !== '')
      return (
        <span
          key={chave}
          className="rounded-full border border-line-soft px-2.5 py-1 text-xs text-ink-3 dark:border-line-soft-dark dark:text-ink-dark-3 min-h-11 sm:min-h-0 inline-flex items-center"
        >
          {rotulo} (0)
        </span>
      );
    return (
      <button
        key={chave}
        type="button"
        onClick={() => onRecortar(aceso ? '' : chave)}
        aria-pressed={aceso}
        className={cn(
          // 🔴 44px de alvo no celular (medido em 28 no beta, 29/09). No
          // desktop volta ao compacto: a régua é de TOQUE, não de mouse.
          'rounded-full border px-2.5 py-1 text-xs transition-colors',
          'min-h-11 sm:min-h-0 inline-flex items-center',
          aceso
            ? 'border-red bg-red text-white'
            : 'border-line-soft text-ink-2 hover:border-red dark:border-line-soft-dark dark:text-ink-dark-2',
        )}
      >
        {rotulo} ({n})
      </button>
    );
  };

  return (
    <div
      className="mb-3 flex flex-wrap items-center gap-1.5"
      role="group"
      aria-label="Filtrar a carteira por vendedor"
      data-testid="carteira-filtro"
    >
      {chip('', 'Todos', total)}
      {chip('sem', 'Sem vendedor', sem)}
      {vendedores.map((u) => chip(u.id, u.nome.split(' ')[0], contagens[u.id] ?? 0))}
    </div>
  );
}

// ─── Aba: Alertas (cortes dos quadros "Precisa de atenção") ────────────

